import { getListingProductIdFromMap } from '../../selectors/productSelectors';
import { LISTING_STATUS } from '../constants/listingStatus';
import { getVariantChannelEntry } from './listingSyncHelpers';

export const SYNC_RECORD_STATE = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
};

const listingStatusForRecordState = (recordState) => (
  recordState === SYNC_RECORD_STATE.PUBLISHED
    ? LISTING_STATUS.PUBLISHED
    : LISTING_STATUS.DRAFT
);

const uniqueStrings = (values) => [...new Set(values.filter(Boolean).map(String))];

const normalizeRecordState = (value) => {
  if (value === SYNC_RECORD_STATE.PUBLISHED || value === 'publish') {
    return SYNC_RECORD_STATE.PUBLISHED;
  }
  if (value === SYNC_RECORD_STATE.DRAFT) {
    return SYNC_RECORD_STATE.DRAFT;
  }
  return null;
};

const isMasterAssetsLoaded = (productsState, product, productId) => (
  Boolean(productId)
  && (
    productsState?.entities?.assets?.masterAssets?.[productId] !== undefined
    || product?.assets !== undefined
  )
);

const isMasterVariantAssetsLoaded = (productsState, variantId) => (
  Boolean(variantId)
  && productsState?.entities?.assets?.masterVariantAssets?.[variantId] !== undefined
);

const isListingAssetsLoaded = (productsState, listingProductId) => (
  Boolean(listingProductId)
  && productsState?.entities?.assets?.listingAssets?.[listingProductId] !== undefined
);

const isListingVariantAssetsLoaded = (productsState, listingVariantId) => (
  Boolean(listingVariantId)
  && productsState?.entities?.assets?.listingVariantAssets?.[listingVariantId] !== undefined
);

const buildMasterLayer = ({
  productId,
  product,
  productsState,
  masterVariantIds,
}) => {
  const variantIds = uniqueStrings(masterVariantIds);
  const productLoaded = isMasterAssetsLoaded(productsState, product, productId);
  const unloadedVariantIds = variantIds.filter(
    (variantId) => !isMasterVariantAssetsLoaded(productsState, variantId),
  );

  if (productLoaded && !unloadedVariantIds.length) {
    return null;
  }

  const productIds = productId ? [String(productId)] : [];
  const searchVariantIds = productLoaded ? unloadedVariantIds : variantIds;
  if (!productIds.length && !searchVariantIds.length) {
    return null;
  }

  return {
    key: 'master',
    productSelection: {
      layer: 'master',
      ...(productIds.length ? { productIds } : {}),
      ...(searchVariantIds.length ? { variantIds: searchVariantIds } : {}),
    },
    masterProductIds: productIds,
    masterVariantEntries: searchVariantIds.map((variantId) => ({
      variantId,
      productId,
    })),
    listingProductEntries: [],
    listingVariantEntries: [],
  };
};

const buildChannelLayer = ({
  recordState,
  productListings,
  productsState,
  masterVariantIds,
}) => {
  const listingStatus = listingStatusForRecordState(recordState);
  const listingProductEntries = [];
  const listingVariantEntries = [];
  const productIds = [];
  const variantIds = [];
  let layerChannelId = null;

  Object.keys(productListings || {}).forEach((channelId) => {
    const listingProductId = getListingProductIdFromMap(productListings, channelId);
    if (!listingProductId) return;

    if (!layerChannelId) {
      layerChannelId = String(channelId);
    }

    if (!isListingAssetsLoaded(productsState, listingProductId)) {
      listingProductEntries.push({ listingProductId, listingStatus });
      productIds.push(listingProductId);
    }

    uniqueStrings(masterVariantIds).forEach((masterVariantId) => {
      const listingVariant = getVariantChannelEntry(
        productsState,
        masterVariantId,
        channelId,
      );
      const listingVariantId = listingVariant?._id;
      if (!listingVariantId || isListingVariantAssetsLoaded(productsState, listingVariantId)) {
        return;
      }

      listingVariantEntries.push({
        listingProductId,
        variantId: listingVariantId,
        listingStatus,
      });
      variantIds.push(listingVariantId);
    });
  });

  const uniqueProductIds = uniqueStrings(productIds);
  const uniqueVariantIds = uniqueStrings(variantIds);
  if (!layerChannelId || (!uniqueProductIds.length && !uniqueVariantIds.length)) {
    return null;
  }

  return {
    key: `channel:${recordState}`,
    productSelection: {
      layer: layerChannelId,
      recordState,
      ...(uniqueProductIds.length ? { productIds: uniqueProductIds } : {}),
      ...(uniqueVariantIds.length ? { variantIds: uniqueVariantIds } : {}),
    },
    masterProductIds: [],
    masterVariantEntries: [],
    listingProductEntries,
    listingVariantEntries,
  };
};

export default function buildSyncMediaPrefetchLayers({
  productId,
  product,
  productsState,
  productListings,
  masterVariantIds = [],
  recordStates = [],
} = {}) {
  const layers = [];
  const masterLayer = buildMasterLayer({
    productId,
    product,
    productsState,
    masterVariantIds,
  });
  if (masterLayer) {
    layers.push(masterLayer);
  }

  [...new Set(recordStates.map(normalizeRecordState).filter(Boolean))].forEach((recordState) => {
    const channelLayer = buildChannelLayer({
      recordState,
      productListings,
      productsState,
      masterVariantIds,
    });
    if (channelLayer) {
      layers.push(channelLayer);
    }
  });

  return layers;
}
